import { Close } from "@mui/icons-material";
import {
  Box,
  Dialog,
  DialogProps,
  DialogTitle,
  IconButton,
  SxProps,
  Theme,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { ReactNode } from "react";
import {
  MobileBottomSheet,
  type MobileBottomSheetSize,
} from "./MobileBottomSheet";

interface IProps {
  children: ReactNode;
  handleClose: Function;
  isOpen: boolean;
  /** @deprecated Bottom sheet é usado automaticamente no mobile (md↓). */
  mobileView?: boolean;
  maxWidth?: DialogProps["maxWidth"];
  /** Título (sheet mobile + Dialog desktop quando informado). */
  sheetTitle?: string;
  sheetSubtitle?: string;
  footer?: ReactNode;
  sheetSize?: MobileBottomSheetSize;
  /** false = mantém Dialog clássico mesmo no mobile */
  useMobileSheet?: boolean;
  /** Estilos extras no Paper do Dialog (ex.: minHeight). */
  paperSx?: SxProps<Theme>;
}

export function CommonDialog({
  children,
  handleClose,
  isOpen,
  maxWidth = "sm",
  sheetTitle,
  sheetSubtitle,
  footer,
  sheetSize = "auto",
  useMobileSheet = true,
  paperSx,
}: IProps) {
  const theme = useTheme();
  const isMobileLayout = useMediaQuery(theme.breakpoints.down("md"));
  const shouldUseSheet = useMobileSheet && isMobileLayout;

  const closeDialog = () => handleClose(null, "iconButtonClick");
  const closeSheet = () => handleClose(null, "backdropClick");

  if (shouldUseSheet) {
    return (
      <MobileBottomSheet
        open={isOpen}
        onClose={closeSheet}
        title={sheetTitle}
        subtitle={sheetSubtitle}
        footer={footer}
        size={sheetSize}
      >
        {children}
      </MobileBottomSheet>
    );
  }

  const handleDialogClose = (_event: unknown, reason: string) => {
    if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
      handleClose(_event, reason);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleDialogClose}
      PaperProps={{
        sx: { borderRadius: "10px", padding: 2, ...paperSx },
      }}
      maxWidth={maxWidth}
      fullWidth
    >
      <IconButton
        onClick={closeDialog}
        sx={{ position: "absolute", right: 5, top: 5 }}
        aria-label="Fechar"
      >
        <Close />
      </IconButton>

      {sheetTitle ? (
        <DialogTitle align="center" sx={{ pr: 5 }}>
          {sheetTitle}
          {sheetSubtitle ? (
            <Box
              component="span"
              sx={{
                display: "block",
                mt: 0.75,
                typography: "body2",
                color: "text.secondary",
                fontWeight: 400,
              }}
            >
              {sheetSubtitle}
            </Box>
          ) : null}
        </DialogTitle>
      ) : null}

      {children}

      {footer ? (
        <Box sx={{ px: 1, pt: 1, pb: 0.5 }}>{footer}</Box>
      ) : null}
    </Dialog>
  );
}
